import { CountryCode } from './explore-map.model';
import { Component, OnInit, SecurityContext } from '@angular/core';
import { ExploreMapService } from './explore-map.service';
import { ActivatedRoute } from '@angular/router';
import { DatasetsService } from './../datasets/datasets.service';
import { DatasetsAtLocationErrorModalComponent } from './components/datasets-at-location-error-modal/datasets-at-location-error-modal.component';
import { IdentifyErrorModalComponent } from './components/identify-error-modal/identify-error-modal.component';
import { UtilsService } from './../../shared/utils/utils.service';
import { APIError } from './../../shared/shared.models';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ModalService } from '../../shared/services/modal.service';

import 'ol/ol.css';
import GML3 from 'ol/format/GML3';
import GeoJSON from 'ol/format/GeoJSON';
import CircleStyle from 'ol/style/Circle';
import Feature from 'ol/Feature';
import Fill from 'ol/style/Fill';
import Map from 'ol/Map';
import Point from 'ol/geom/Point';
import * as olProj from 'ol/proj';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import View from 'ol/View';
import { WMSSettings, WMTSSettings, FeatureInfo } from './explore-map.model';
import ScaleLine from 'ol/control/ScaleLine';
import { DomSanitizer } from "@angular/platform-browser";

@Component({
  selector: 'app-explore-map',
  templateUrl: './explore-map.component.html',
  styleUrls: ['./explore-map.component.scss'],
})
export class ExploreMapComponent implements OnInit {
  activeMapProxyLayername: string = '';
  apiError: APIError = new APIError();
  attribution: string = '';
  dataProjection = 'EPSG:4326';
  datasetsByLocationActive: boolean = false;
  defaultZoom: number = 5;
  featuresInfo: any = [];
  gazetteerVectorSource = new VectorSource();
  identifyFeatureActive: boolean = false;
  countryAtCoordinates: CountryCode;
  map: any;
  mapProjection = 'EPSG:3857';
  mapService: any;
  mapServiceSlug: string;
  maxZoom: any;
  maxZoomReached: boolean = false;
  minZoom: any;
  minZoomReached: boolean = false;
  onDestroy$ = new Subject();
  overlayVisible: boolean = false;
  backgroundVisible: boolean = false;

  identifyLoading: boolean = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private datasetsService: DatasetsService,
    private utilsService: UtilsService,
    private exploreMapService: ExploreMapService,
    private modalService: ModalService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit() {
    this.activatedRoute.paramMap.pipe(takeUntil(this.onDestroy$)).subscribe((params) => {
      this.mapServiceSlug = params.get('mapServiceSlug') || 'egm-erm-basemap';
      this.setMapService();
    });
    setTimeout(() => {
      this.initMap(), 0;
    });
  }

  /**
   * @returns void
   * set the current mapService depending on the mapService slug param, or if coming
   * from the generic /explore-map route, load the ERM and EGM stack
   */
  setMapService(): void {
    this.datasetsService
      .getMapServiceDetails(this.mapServiceSlug)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (data) => {
          this.mapService = data;
          if (
            data.mapServiceMetadata &&
            data.mapServiceMetadata.minZoom &&
            data.mapServiceMetadata.maxZoom
          ) {
            this.minZoom = parseInt(data.mapServiceMetadata.minZoom, 10);
            this.maxZoom = parseInt(data.mapServiceMetadata.maxZoom, 10);
          } else {
            this.minZoom = 0;
            this.maxZoom = 17;
          }
        },
        (error) => {
          this.apiError = this.utilsService.handleAPIError(error);
        },
        () => {
          this.resetMapInterface();
          this.setAttribution();
          if (this.mapServiceSlug === 'pan-european-imagery') {
            this.initImageryLayer();
          } else if (this.mapServiceSlug === 'open-gazetteer') {
            this.initOMELayers();
            this.initBackgroundLayer();
            this.initBackgroundLabelLayer();
          } else if (this.mapServiceSlug === 'cadastral-all') {
            /**
             * The cadastral-all OME service will be using
             * same background_labels + background_greyscale basemaps
             * therefore we call same layers here as for ome-gazetteer
             */
            this.initOMELayers();
            this.initBackgroundLayer();
            this.initBackgroundLabelLayer();
          }
          else {
            this.initOMELayers();
          }
          this.setMinMaxZoom();
        },
      );
  }

  /**
   * @returns void
   * Initialise the map only once with placeholder layers
   */
  initMap(): void {
    var view = new View({ enableRotation: false });
    this.map = new Map({
      target: 'map-container',
      controls: [],
      layers: [
        new VectorLayer({
          source: null,
          properties: {
            name: 'gazetteer',
          },
        }),
        new TileLayer({
          source: null,
          properties: {
            name: 'overlay-layer',
          },
        }),
        new TileLayer({
          source: null,
          properties: {
            name: 'dataset-layer',
          },
        }),
        new TileLayer({
          source: null,
          properties: {
            name: 'background-layer',
          },
        }),
        new TileLayer({
          source: null,
          properties: {
            name: 'gazetteer-background-labels',
          },
        }),
      ],
      view: view,
    });
    this.initOverlayLayer;
    this.captureDoubleClickZoomEvent();
    this.setToDefaultExtent();
    this.initOverlayLayer();
    const scaleLineControl = new ScaleLine();
    this.map.addControl(scaleLineControl);
  }

  /**
   * @returns boolean
   */
  get allowOverlay(): boolean {
    if (this.mapServiceSlug === 'euro-dem' || this.mapServiceSlug === 'pan-european-imagery') {
      return true;
    } else {
      return false;
    }
  }

  get allowLegend(): boolean {
    if (
      this.mapServiceSlug === 'pan-european-imagery'
    ) {
      return false;
    } else {
      return true;
    }
  }

  /**
   * @returns boolean
   */
  get allowIdentify(): boolean {
    if (
      this.mapServiceSlug === 'pan-european-imagery' ||
      this.mapServiceSlug === 'euro-dem' ||
      this.mapServiceSlug === 'egm-erm-basemap'
    ) {
      return false;
    } else {
      return true;
    }
  }

  captureDoubleClickZoomEvent(): void {
    this.map.on('dblclick', (evt) => {
      evt.preventDefault();
      this.zoomIn();
    });
  }

  /**
   * @returns void
   * Load the overlay layer that is optionally displayed on top of the EuroDem and Imagery layers
   * to help users orientate themselves.
   */
  initOverlayLayer(): void {
    const wmtsSettings = new WMTSSettings();
    wmtsSettings.layerName = 'ome_overlay';
    wmtsSettings.url =
      'maps/wmts/' +
      wmtsSettings.layerName +
      '/{TileMatrixSet}/{TileMatrix}/{TileCol}/{TileRow}.png?token=' +
      environment.elsToken;
    this.addWMTSLayer(wmtsSettings);
  }

  /**
   * @returns void
   * Load the background layer that is displayed below the gazetteer layer
   * to help users orientate themselves.
   */
  initBackgroundLayer(): void {
    const wmtsSettings = new WMTSSettings();
    wmtsSettings.layerName = 'background_greyscale';
    wmtsSettings.url =
      'maps/wmts/' +
      wmtsSettings.layerName +
      '/{TileMatrixSet}/{TileMatrix}/{TileCol}/{TileRow}.png?token=' +
      environment.elsToken;
    this.addWMTSLayer(wmtsSettings);
  }

  /**
   * @returns void
   * Load the background labels layer that is displayed below the gazetteer layer
   * to help users orientate themselves.
   */
  initBackgroundLabelLayer(): void {
    const wmtsSettings = new WMTSSettings();
    wmtsSettings.layerName = 'background_labels';
    wmtsSettings.url =
      'maps/wmts/' +
      wmtsSettings.layerName +
      '/{TileMatrixSet}/{TileMatrix}/{TileCol}/{TileRow}.png?token=' +
      environment.elsToken;
    this.addWMTSLayer(wmtsSettings);
  }

  initImageryLayer(): void {
    const wmsSettings = new WMSSettings();
    wmsSettings.layerName = 'sen2eu:rgb';
    wmsSettings.url = this.mapService.externalServiceURL;
    this.exploreMapService.wmsSource = new TileWMS({
      params: {
        LAYERS: wmsSettings.layerName,
      },
      url: wmsSettings.url,
      crossOrigin: 'anonymous',
    });
    this.map.getLayers().forEach((layer) => {
      if (layer && layer.get('name') === 'dataset-layer') {
        this.activeMapProxyLayername = wmsSettings.layerName;
        layer.setSource(this.exploreMapService.wmsSource);
        layer.setZIndex(-2);
      }
    });
  }

  /**
   * @returns void
   * Following is called onInit or whenever the product changes.
   * WMTS is used for the background map source and layer
   * WMS is only used as a source for simplifying the Identify functionality.
   */
  initOMELayers(): void {
    const wmsSettings = new WMSSettings();
    wmsSettings.layerName = this.mapService.mapproxyLayerName;
    wmsSettings.url = 'maps/wms' + '?token=' + environment.elsToken;
    this.exploreMapService.addWMSSource(wmsSettings);

    const wmtsSettings = new WMTSSettings();
    wmtsSettings.layerName = this.mapService.mapproxyLayerName;
    wmtsSettings.url = `maps/wmts/${wmtsSettings.layerName}/{TileMatrixSet}/{TileMatrix}/{TileCol}/{TileRow}.png?token=${environment.elsToken}`;
    this.addWMTSLayer(wmtsSettings);
  }

  /**
   * Add WMTS Layer
   * @param wmtsSettings
   */
  addWMTSLayer(wmtsSettings: WMTSSettings): void {
    let wmtsSource = this.exploreMapService.getWMTSSource(wmtsSettings);

    /**
     * overlay-layer only gets loaded once on init. Any subsequent
     * layers are datasets products, so get populated as the source of the
     * dataset-layer
     */
    if (wmtsSettings.layerName === 'ome_overlay') {
      this.map.getLayers().forEach((layer) => {
        if (layer && layer.get('name') === 'overlay-layer') {
          layer.setSource(wmtsSource);
          layer.setZIndex(-1);
          layer.setVisible(false);
        }
      });
    } else if (wmtsSettings.layerName === 'background_greyscale') {
      this.map.getLayers().forEach((layer) => {
        if (layer && layer.get('name') === 'background-layer') {
          layer.setSource(wmtsSource);
          layer.setZIndex(-3);
          layer.setVisible(true);
          layer.setOpacity(0.2);
        }
      });
    } else if (wmtsSettings.layerName === 'background_labels') {
      this.map.getLayers().forEach((layer) => {
        if (layer && layer.get('name') === 'gazetteer-background-labels') {
          layer.setSource(wmtsSource);
          layer.setZIndex(-3);
          layer.setVisible(true);
          layer.setOpacity(0.5);
        }
      });
    } else {
      this.map.getLayers().forEach((layer) => {
        if (layer && layer.get('name') === 'dataset-layer') {
          this.activeMapProxyLayername = wmtsSource.getLayer();
          layer.setSource(wmtsSource);
          layer.setZIndex(-2);
        }
      });
    }

    this.addMoveEndListener();
  }

  /**
   * @returns void
   * Event listener to check the zoom level limits for products and
   * to change the attribution for the ERM / EGM stack based on current zoom level
   */
  addMoveEndListener(): void {
    const onMoveEnd = () => {
      this.setZoomLimitWarnings();
      if (this.mapServiceSlug === 'egm-erm-basemap') {
        this.setAttribution();
      }
    };
    this.map.on('moveend', onMoveEnd);
  }

  /**
   * @returns void
   * Whenever mapservice / dataset is changed, reset all the boolean defaults
   */
  resetMapInterface(): void {
    this.datasetsByLocationActive = false;
    this.identifyFeatureActive = false;
    this.maxZoomReached = false;
    this.minZoomReached = false;
    this.overlayVisible = false;
    this.setBackgroundVisibility();
    this.setOverlayVisibility();
  }

  /**
   * @returns void
   * Return wms source info for that pixel when map is clicked
   */
  toggleIdentifyFeature(): void {
    this.identifyFeatureActive = !this.identifyFeatureActive;
    if (this.identifyFeatureActive) {
      this.datasetsByLocationActive = false;
    }
    this.map.on('singleclick', (evt) => {
      if (this.identifyFeatureActive) {
        this.identifyFeatureActive = false;
        const view = this.map.getView();
        const viewResolution = /** @type {number} */ view.getResolution();
        const url = this.exploreMapService.wmsSource.getFeatureInfoUrl(
          evt.coordinate,
          viewResolution,
          this.mapProjection,
          { INFO_FORMAT: 'application/vnd.ogc.gml', FEATURE_COUNT: 50 },
        );
        if (url) {
          this.identifyLoading = true;
          this.exploreMapService
            .getTileSourceFeature(url)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (data) => {
                let xml_split = data.split('\n');
                let gmlFeatures = new GML3();
                let gmlFeaturesList = [];
                for (let layer of xml_split) {
                  gmlFeaturesList.push(gmlFeatures.readFeatures(layer));
                }
                data = new GeoJSON();
                // @ts-ignore
                data = JSON.parse(data.writeFeatures(gmlFeaturesList.flat()));
                this.featuresInfo = [];
                data.features.map((feature) => {
                  const featureInfo = new FeatureInfo();
                  featureInfo.layerName = feature.id;
                  featureInfo.properties = [];
                  Object.keys(feature.properties).forEach((key) => {
                    if (!['boundedBy', 'referencepoint', 'wkb_geometry', 'geom'].includes(key)) {
                      // get the property value
                      let propValue = this.replaceUrlWithLinks(feature.properties[key]);
                      const property = {
                        property: key,
                        value: propValue,
                      };
                      featureInfo.properties.push(property);
                    }
                  });
                  this.featuresInfo.push(featureInfo);
                });
              },
              error: (data) => {
                this.modalService.openModal(IdentifyErrorModalComponent);
                this.identifyFeatureActive = true;
                this.identifyLoading = false;
              },
              complete: () => {
                if (this.featuresInfo && this.featuresInfo.length > 0) {
                  document.getElementById('identifyFeatureHiddenTrigger').click();
                }
                this.identifyLoading = false;
              },
            });
        }
      }
    });
  }

  /**
   * replace Urls in a property value with links
   * @param propValue a feature info property value
   * @returns modified string
   */
  replaceUrlWithLinks(propValue: string) {
    let regex = new RegExp(/https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)/gi);
    // replace all url matches with a hyperlink
    propValue = propValue.replace(regex,  (match: string) => {
      // sanitize the URL
      propValue = this.sanitizer.sanitize(SecurityContext.URL, match)
      // wrap with a hyperlink
      return `<a href="${match}" target="_blank">${match}</a>`;
    });
    return propValue;
  }

  /**
   * @returns void
   */
  toggleOverlay(): void {
    this.overlayVisible = !this.overlayVisible;
    if (this.overlayVisible) {
      this.identifyFeatureActive = false;
      this.datasetsByLocationActive = false;
    }
    this.setOverlayVisibility();
  }

  /**
   * @returns void
   * Below gets called from toggleOverlay function as well as
   * whenever the map is reset (to hide the overlay layer again)
   */
  setOverlayVisibility(): void {
    this.map.getLayers().forEach((layer) => {
      if (layer && layer.get('name') === 'overlay-layer') {
        if (this.overlayVisible) {
          layer.setVisible(true);
        } else {
          layer.setVisible(false);
        }
      }
    });
  }

  /**
   * @returns void
   * Below gets called from toggleOverlay function as well as
   * whenever the map is reset (to hide the overlay layer again)
   */
  setBackgroundVisibility(): void {
    this.map.getLayers().forEach((layer) => {
      if (
        layer &&
        (layer.get('name') === 'background-layer' ||
          layer.get('name') === 'gazetteer-background-labels')
      ) {
        if (this.backgroundVisible) {
          layer.setVisible(true);
        } else {
          layer.setVisible(false);
        }
      }
    });
  }

  /**
   * @param  {} event
   * @returns void
   * Below function is called on programmatic click of hidden trigger that opens the
   * off-canvas identify panel. Need to prevent bubbling of the event so the window doesn't
   * open and then close again.
   */
  onIdentifyTriggerClick(event): void {
    event.stopPropagation();
    event.preventDefault();
  }

  /**
   * @returns void
   * Toggle the DatasetsByLocation boolean and when map is clicked, if true then
   * get the current location country code and open the side panel.
   */
  toggleDatasetsByLocation(): void {
    this.datasetsByLocationActive = !this.datasetsByLocationActive;
    if (this.datasetsByLocationActive) {
      this.identifyFeatureActive = false;
    }
    this.map.on('singleclick', (evt) => {
      if (this.datasetsByLocationActive) {
        this.datasetsByLocationActive = false;
        let coordinates = olProj.transform(evt.coordinate, 'EPSG:3857', 'EPSG:4326');

        this.exploreMapService
          .getLocationCountryCode(coordinates)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (data) => {
              this.countryAtCoordinates = data;
            },
            error: () => {
              this.modalService.openModal(DatasetsAtLocationErrorModalComponent);
              this.datasetsByLocationActive = true;
            },
            complete: () => {
              document.getElementById('availableDatasetsTrigger').click();
            },
          });
      }
    });
  }

  /**
   * @returns void
   * Set initial extent and zoom when map is created or whenever the reset extent button is clicked
   */
  setToDefaultExtent(): void {
    let defaultExtent = {
      center: olProj.fromLonLat([10, 50]),
      zoom: this.defaultZoom,
      duration: 100,
    };
    let view = this.map.getView();
    view.animate(defaultExtent);
  }

  /**
   * @returns void
   * Sets the attribution based on the mapService name. Or whenever the
   * ERM / EGM stack is the currently loaded product, the map moveend event is used
   * to call this function that checks zoom level and flip the attribution depending on the zoom level
   */
  setAttribution(): void {
    if (this.mapService.mapServiceURLSlug === 'egm-erm-basemap') {
      const view = this.map.getView();
      const currentZoom = view.getZoom();
      const productName = currentZoom >= 10 ? 'EuroRegionalMap' : 'EuroGlobalMap';
      this.attribution =
        '&copy; EuroGeographics | &copy; Cartography derived from NGI Belgium | ' + productName;
    } else if (
      this.mapService.mapServiceURLSlug === 'euro-global-map' ||
      this.mapService.mapServiceURLSlug === 'euro-regional-map'
    ) {
      this.attribution =
        '&copy; EuroGeographics | &copy; Cartography derived from NGI Belgium | ' +
        this.mapService.name;
    } else if (this.mapService.mapServiceURLSlug === 'pan-european-imagery') {
      const currentYear = new Date().getFullYear();
      this.attribution = `&copy; European Union, contains Copernicus Sentinel-2 data ${currentYear}, processed by the Federal Agency for Cartography and Geodesy (<a href="http://www.bkg.bund.de/" target="_blank">BKG<i class="bi bi-box-arrow-up-right ms-1"></i
            ></a>)`;
    } else {
      this.attribution = '&copy; EuroGeographics | ' + this.mapService.name;
    }
  }

  /**
   * @returns void
   * Following is called whenever the mapService changes as min and max zoom area taken from the metadata.
   * We use these props to set the min and max zoom of the map.
   */
  setMinMaxZoom(): void {
    let view = this.map.getView();
    view.setMaxZoom(this.maxZoom);
    view.setMinZoom(this.minZoom);
  }

  /**
   * @returns void
   * Called from map on moveend event. Sets minZoomReached or maxZoomReached variables to control
   * button disabling whenever limits are reached. Also sets to nearest available zoom for product
   * if out-with the limit.
   */
  setZoomLimitWarnings(): void {
    const view = this.map.getView();
    const zoom = view.getZoom();
    if (zoom < this.maxZoom) {
      this.maxZoomReached = false;
    } else {
      this.maxZoomReached = true;
      view.setZoom(this.maxZoom);
    }
    if (zoom > this.minZoom) {
      this.minZoomReached = false;
    } else {
      this.minZoomReached = true;
      view.setZoom(this.minZoom);
    }
  }

  /**
   * @returns void
   */
  zoomIn(): void {
    const view = this.map.getView();
    if (!this.maxZoomReached) {
      view.animate({ zoom: view.getZoom() + 1 });
    }
  }

  /**
   * @returns void
   */
  zoomOut(): void {
    const view = this.map.getView();
    if (!this.minZoomReached) {
      view.animate({ zoom: view.getZoom() - 1 });
    }
  }

  /**
   * @returns void
   */
  resetExtent(): void {
    this.setToDefaultExtent();
  }

  /**
   * @param  {} coordinates
   * @returns void
   * Called on Gazetteer placename selection
   * If the current zoom of the map is less than maxZoom of the product, zoom in to the maxZoom.
   */
  centerOnCoordinates(coordinates): void {
    const view = this.map.getView();
    const currentZoom = view.getZoom();
    const calculatedZoom = currentZoom > this.maxZoom ? currentZoom : this.maxZoom;
    let coordinatesExtent = {
      center: coordinates,
      zoom: calculatedZoom,
      duration: 100,
    };
    view.animate(coordinatesExtent);
  }

  /**
   * @returns void
   * Clear Gazetteer map circle features
   */
  onClearGazetteerResult(): void {
    this.gazetteerVectorSource.clear();
  }

  /**
   * @param  {any} result
   * @returns void
   * Draw a circle on the map corresponding to selected Gazetteer result coordinates
   */
  onSelectGazetteerResult(result: any): void {
    var circle = new Point(olProj.fromLonLat([parseFloat(result.x), parseFloat(result.y)]));
    var circleFeature = new Feature(circle);
    circleFeature.setStyle(
      new Style({
        image: new CircleStyle({
          radius: 15,
          fill: new Fill({ color: '#f3e941' }),
          stroke: new Stroke({
            color: 'white',
            width: 2,
          }),
        }),
      }),
    );
    this.map.getLayers().forEach((layer) => {
      if (layer && layer.get('name') === 'gazetteer') {
        this.gazetteerVectorSource.clear();
        this.gazetteerVectorSource.addFeature(circleFeature);
        layer.setSource(this.gazetteerVectorSource);
      }
    });
    let coordinates = olProj.fromLonLat([parseFloat(result.x), parseFloat(result.y)]);
    this.centerOnCoordinates(coordinates);
  }

  /**
   * @returns boolean
   */
  get loading(): boolean {
    return this.datasetsService.datasetsLoading;
  }

  /**
   * @returns void
   */
  ngOnDestroy(): void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }
}
